/**
 * <SEO> — per-route SEO meta + JSON-LD schemas.
 *
 * Usage:
 *   <SEO
 *     title="Worksheet Generator | ActanonEdu"
 *     description="..."
 *     path="/worksheet-generator"
 *     breadcrumbs={[{name:'Home',url:'/'},{name:'Worksheet Generator',url:'/worksheet-generator'}]}
 *     faq={[{q:'…',a:'…'}, …]}
 *     article={{title, datePublished, dateModified, image, author}}
 *     keywords={['…','…']}
 *   />
 *
 * NOTE: WhatsApp/Facebook/LinkedIn crawlers DO NOT execute JavaScript, so they
 * read the static OG tags from /public/index.html. The tags injected by this
 * component are still valuable for clients that run JS (Twitter card validator,
 * SERP snippets, evolved crawlers), and for the on-page accessibility benefits.
 */
import { Helmet } from 'react-helmet-async';

const SITE = 'https://actanonedu.com';
const DEFAULT_OG_IMAGE = `${SITE}/actanon-logo-512.png?v=2`;

export default function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  keywords = [],
  breadcrumbs = [],
  faq = [],
  article = null,
  type = 'website',
  noindex = false,
}) {
  const canonical = `${SITE}${path}`;
  const fullTitle = title?.includes('ActanonEdu')
    ? title
    : title
    ? `${title} | ActanonEdu`
    : 'ActanonEdu | AI Teaching Tools & Academic Intelligence';

  const schemas = [];

  if (breadcrumbs.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((b, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: b.name,
        item: `${SITE}${b.url}`,
      })),
    });
  }

  if (faq.length > 0) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  if (article) {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title || fullTitle,
      description: article.description || description,
      datePublished: article.datePublished,
      dateModified: article.dateModified || article.datePublished,
      image: article.image || image,
      author: { '@type': 'Person', name: article.author || 'ActanonEdu Team' },
      publisher: {
        '@type': 'Organization',
        name: 'ActanonEdu',
        logo: { '@type': 'ImageObject', url: `${SITE}/actanon-logo-512.png` },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    });
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="ActanonEdu" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={`${title || 'ActanonEdu'} — ActanonEdu`} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />

      {/* JSON-LD schemas */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
